import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, Calendar, DollarSign, Target, Zap, TrendingUp, User, Activity } from 'lucide-react';
import type { Player } from '../../types/player';
import { translatePosition } from '../../utils/positionTranslations';

interface PlayerListItemProps {
  player: Player;
  onViewProfile?: (playerId: string | number) => void;
}

const PlayerListItem: React.FC<PlayerListItemProps> = ({ player, onViewProfile }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'es' | 'en';

  // Helper functions (same as PlayerCard)
  const getPlayerName = () => {
    const name = player.player_name || player.nombre_transfermarkt || t('players.noName');
    const cleanName = name.split('(')[0].trim();
    return cleanName || name;
  };

  const getPosition = () => {
    const position = player.position || player.posicion_transfermarkt || 'N/A';
    return translatePosition(position, currentLang);
  };

  const getBirthDate = () => {
    return player.date_of_birth || player.fecha_nacimiento_transfermarkt;
  };

  const getClubName = () => {
    return player.current_club_name || player.equipo_transfermarkt || t('players.noTeam');
  };

  const getContractExpiry = () => {
    return player.contract_expires || player.fecha_fin_contrato;
  };

  const getImageUrl = () => {
    return player.player_image_url || player.url_foto_jugador;
  };

  const getAge = (birthDate?: string) => {
    if (!birthDate) return 'N/A';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `€${(amount / 1000000).toFixed(1)}M`;
    }
    return `€${(amount / 1000).toFixed(0)}K`;
  };

  const formatContractEnd = (contractEnd?: string) => {
    if (!contractEnd) return t('players.notAvailable');
    const date = new Date(contractEnd);
    const today = new Date();
    const monthsLeft = Math.round((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24 * 30));
    
    if (monthsLeft < 0) return 'Expirado';
    if (monthsLeft < 6) return `${monthsLeft}m`;
    
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short'
    });
  };

  const getPositionColor = () => {
    const pos = getPosition().toLowerCase();
    if (pos.includes('goalkeeper') || pos.includes('portero')) {
      return 'bg-gradient-to-r from-amber-500 to-amber-600';
    }
    if (pos.includes('defen') || pos.includes('back')) {
      return 'bg-gradient-to-r from-blue-500 to-blue-600';
    }
    return 'bg-gradient-to-r from-indigo-500 to-indigo-600';
  };

  const handleViewProfile = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const playerId = player.player_id || player.id_fbref || player.id_transfermarkt?.toString();
    
    if (!playerId) {
      console.error('No se encontró ID válido para el jugador:', player);
      return;
    }

    if (onViewProfile) {
      onViewProfile(playerId);
    } else {
      navigate(`/sporting/player-detail/${playerId}`);
    }
  };

  return (
    <div className="group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
      {/* Hover gradient effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-purple-500/0 to-indigo-500/0 group-hover:from-blue-500/5 group-hover:via-purple-500/5 group-hover:to-indigo-500/5 transition-all duration-500 pointer-events-none"></div>
      
      <div className="relative flex items-center p-4 gap-4">
        {/* Player Photo */}
        <div className="flex-shrink-0">
          <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden bg-gradient-to-br from-slate-200 to-slate-300 border-2 border-gray-200 shadow-md group-hover:scale-105 transition-transform duration-300">
            {getImageUrl() ? (
              <img 
                src={getImageUrl()} 
                alt={getPlayerName()}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="w-10 h-10 md:w-12 md:h-12 text-slate-400" />
              </div>
            )}
          </div>
        </div>

        {/* Player Info - Main Section */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-2">
            {/* Name and Position */}
            <div className="flex-1 min-w-0">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 truncate mb-1">
                {getPlayerName()}
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`${getPositionColor()} text-white text-xs font-bold px-3 py-1 rounded-full shadow-md`}>
                  {getPosition()}
                </span>
                {player.has_enriched_data && (
                  <span className="bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-md flex items-center gap-1">
                    <Activity className="w-3 h-3" />
                    <span>PRO</span>
                  </span>
                )}
              </div>
            </div>

            {/* Market Value Badge */}
            {player.valor_mercado && (
              <div className="flex-shrink-0">
                <div className="bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg flex items-center gap-1.5">
                  <DollarSign className="w-4 h-4" />
                  <span>{formatCurrency(player.valor_mercado)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mt-3">
            {/* Age */}
            <div className="bg-slate-50 rounded-lg p-2 border border-slate-200">
              <div className="text-[10px] font-semibold text-slate-600 mb-0.5 uppercase tracking-wide flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>{t('players.age')}</span>
              </div>
              <div className="text-sm font-bold text-slate-900">
                {getAge(getBirthDate())}
              </div>
            </div>

            {/* Goals */}
            {(player.goles !== undefined && player.goles !== null) && (
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg p-2 border-2 border-emerald-200">
                <div className="text-[10px] font-semibold text-emerald-700 mb-0.5 uppercase tracking-wide flex items-center gap-1">
                  <Target className="w-3 h-3" />
                  <span>{t('players.goals')}</span>
                </div>
                <div className="text-sm font-bold text-emerald-900">
                  {player.goles}
                </div>
              </div>
            )}

            {/* Assists */}
            {(player.asistencias !== undefined && player.asistencias !== null) && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-2 border-2 border-blue-200">
                <div className="text-[10px] font-semibold text-blue-700 mb-0.5 uppercase tracking-wide flex items-center gap-1">
                  <Zap className="w-3 h-3" />
                  <span>{t('players.assistsShort')}</span>
                </div>
                <div className="text-sm font-bold text-blue-900">
                  {player.asistencias}
                </div>
              </div>
            )}

            {/* Minutes */}
            {(player.minutos_jugados !== undefined && player.minutos_jugados !== null) && (
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-2 border-2 border-orange-200">
                <div className="text-[10px] font-semibold text-orange-700 mb-0.5 uppercase tracking-wide flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  <span>Min</span>
                </div>
                <div className="text-sm font-bold text-orange-900">
                  {player.minutos_jugados > 1000 ? `${(player.minutos_jugados / 1000).toFixed(1)}K` : player.minutos_jugados}
                </div>
              </div>
            )}

            {/* Contract */}
            {getContractExpiry() && (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-2 border-2 border-purple-200">
                <div className="text-[10px] font-semibold text-purple-700 mb-0.5 uppercase tracking-wide">
                  {t('players.contract')}
                </div>
                <div className="text-sm font-bold text-purple-900">
                  {formatContractEnd(getContractExpiry())}
                </div>
              </div>
            )}

            {/* Club */}
            <div className="bg-blue-50 rounded-lg p-2 border-2 border-blue-200 col-span-2 md:col-span-1">
              <div className="text-[10px] font-semibold text-blue-700 mb-0.5 uppercase tracking-wide">
                {t('players.club')}
              </div>
              <div className="text-xs font-bold text-blue-900 truncate" title={getClubName()}>
                {getClubName()}
              </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="flex-shrink-0">
          <button
            onClick={handleViewProfile}
            type="button"
            className="bg-gradient-to-r from-indigo-600 via-blue-600 to-purple-600 hover:from-indigo-700 hover:via-blue-700 hover:to-purple-700 text-white font-bold p-3 rounded-xl transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-110 active:scale-95"
            title={t('players.viewProfile')}
          >
            <Eye className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlayerListItem;
